import React, { CSSProperties, useCallback } from 'react';
import { useEditor } from '../hooks/use-editor';

import { ProseMirror } from 'use-prosemirror';
import css from './styles.module.css';
import debounce from 'lodash.debounce';
import { useEditorImageHandlers } from '../hooks/use-editor-image-handlers';
import { Menubar } from './Menubar';
import { ViewProvider } from '../hooks/use-view';
import { AttributeProps, Attributes } from './Attributes';
import { Alert } from '@mm/components';
import { EditorState } from 'prosemirror-state';
import { getHTMLStringFromState } from '../utils';
import { Schema } from 'prosemirror-model';

interface EditorProps {
  value?: string;
  attributes?: AttributeProps['attributes'];
  attachments?: [];
  styleEditor?: CSSProperties;
  styleMenubar?: CSSProperties;
  onChange: (state: EditorState) => void;
  schema?: Schema<any, any>;
}

const styleEditorDefaults = {
  minHeight: 200,
};

export const Editor: React.FC<EditorProps> = ({
  value = '',
  attributes,
  attachments = [],
  styleEditor,
  styleMenubar,
  onChange = () => {},
  schema,
}) => {
  const editor = useEditor(value, schema);

  const allEditorStyles = { ...styleEditorDefaults, ...styleEditor };

  const {
    handleDrop,
    handlePaste,
    onImageSelected,
    imagesScaledDown,
    maxImageWidth,
    maxImageHeight,
  } = useEditorImageHandlers(editor?.ref?.current?.view);

  const onClick = (run, focusAfter = true) => {
    if (editor.state && editor.dispatch) {
      run(editor.state, editor.dispatch);

      if (focusAfter) {
        setTimeout(() => editor.focus(), 10);
      }
    }
  };

  const promoteChange = useCallback(
    debounce((state: EditorState) => {
      onChange(state);
    }, 50),
    [onChange],
  );

  const handleEditorChange = (state: EditorState) => {
    editor.setState(state);
    promoteChange(state);
  };

  return (
    <ViewProvider
      value={{
        state: editor.state,
        dispatch: editor.dispatch,
        focus: editor.focus,
      }}
    >
      <div className={css.container}>
        <Menubar
          onClick={onClick}
          onUpload={onImageSelected}
          style={styleMenubar}
        />

        {attributes && <Attributes attributes={attributes} />}

        <ProseMirror
          ref={editor.ref}
          className={css.editor}
          state={editor.state}
          onChange={handleEditorChange}
          style={allEditorStyles}
          handleDrop={handleDrop}
          handlePaste={handlePaste}
        />

        {imagesScaledDown && (
          <Alert
            type="warning"
            showCloseIcon={false}
            className={css.bottomAlert}
          >
            Large images are automatically scaled down to a maximum width of{' '}
            {maxImageWidth}px and a maximum height of {maxImageHeight}px.
          </Alert>
        )}
      </div>
    </ViewProvider>
  );
};
